/**
 * Access tokeny vázané na GitLab uživatele nebo projekt.
 * Tool: gitlab.token_manage. Plaintext jen jednou po create.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Copy, KeyRound, Plus, Trash2 } from 'lucide-react';
import { Badge, Button, Input } from '../../../components/ui';
import { callWidgetTool } from '../../../widgets/scope';
import type { EffectiveScope } from '../../../widgets/types';
import { themedConfirm } from '../../../lib/themed-dialogs';

interface TokenRow {
  id: number;
  name: string;
  active: boolean;
  revoked: boolean;
  scopes: string[];
  expires_at: string | null;
  last_used_at: string | null;
}

export function TokensBlock({
  scope,
  connectionId,
  target,
  targetId,
}: {
  scope: EffectiveScope;
  connectionId?: string;
  target: 'user' | 'project';
  targetId: number;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [plaintext, setPlaintext] = useState<string | null>(null);
  const key = ['gitlab-tokens', target, targetId, connectionId ?? ''];
  const conn = connectionId ? { connection_id: connectionId } : {};

  const q = useQuery<{ ok?: boolean; error?: string; tokens?: TokenRow[] }>({
    queryKey: key,
    queryFn: () =>
      callWidgetTool(
        'gitlab.token_manage',
        { action: 'list', target, target_id: targetId, ...conn },
        scope,
        { silent: true },
      ),
    enabled: targetId > 0,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: key });

  const mut = useMutation({
    mutationFn: (args: { action: 'create' | 'revoke'; name?: string; token_id?: number }) =>
      callWidgetTool<{ ok?: boolean; error?: string; plaintext?: string | null }>(
        'gitlab.token_manage',
        {
          action: args.action,
          target,
          target_id: targetId,
          ...(args.name ? { name: args.name } : {}),
          ...(args.token_id ? { token_id: args.token_id } : {}),
          ...conn,
        },
        scope,
      ),
    onSuccess: (r, vars) => {
      if (r?.ok === false) {
        toast.error(r.error ?? t('gitlab.tokens.failed'));
        return;
      }
      if (vars.action === 'create' && r.plaintext) setPlaintext(r.plaintext);
      invalidate();
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const onRevoke = async (tok: TokenRow) => {
    const ok = await themedConfirm({
      title: t('gitlab.tokens.revokeTitle', { name: tok.name }),
      description: t('gitlab.tokens.revokeDesc'),
      confirmLabel: t('gitlab.tokens.revokeConfirm'),
    });
    if (!ok) return;
    mut.mutate({ action: 'revoke', token_id: tok.id });
  };

  const tokens = q.data?.tokens ?? [];

  return (
    <div className='rounded-lg border border-zinc-800 bg-zinc-950/40 p-3 space-y-2'>
      <div className='text-xs text-zinc-400 flex items-center gap-1'>
        <KeyRound size={12} />
        {t('gitlab.tokens.title', { count: tokens.length })}
      </div>
      {plaintext && (
        <div className='rounded border border-amber-500/30 bg-amber-500/10 p-2 text-xs space-y-1'>
          <div className='text-amber-200'>{t('gitlab.tokens.plaintextOnce')}</div>
          <div className='flex items-center gap-2'>
            <code className='font-mono break-all text-amber-100 flex-1'>{plaintext}</code>
            <Button
              variant='ghost'
              size='sm'
              onClick={() => void navigator.clipboard.writeText(plaintext)}
            >
              <Copy size={12} />
            </Button>
          </div>
        </div>
      )}
      {q.isLoading
        ? <div className='text-xs text-zinc-500'>{t('common.loading')}</div>
        : q.data?.ok === false
        ? <div className='text-xs text-red-400'>{q.data.error ?? t('gitlab.tokens.failed')}</div>
        : tokens.length === 0
        ? <div className='text-xs text-zinc-500'>{t('gitlab.tokens.empty')}</div>
        : (
          <div className='space-y-1'>
            {tokens.map((tok) => (
              <div key={tok.id} className='flex items-center gap-2 text-sm'>
                <span className='min-w-0 flex-1 truncate font-medium text-zinc-200'>{tok.name}</span>
                <Badge variant={tok.active ? 'green' : 'gray'}>
                  {tok.active ? t('gitlab.tokens.active') : t('gitlab.tokens.revoked')}
                </Badge>
                <span className='text-[10px] text-zinc-500 truncate max-w-[10rem]'>
                  {(tok.scopes ?? []).join(', ')}
                </span>
                <Button
                  variant='ghost'
                  size='sm'
                  disabled={mut.isPending || !tok.active}
                  onClick={() => void onRevoke(tok)}
                >
                  <Trash2 size={13} />
                </Button>
              </div>
            ))}
          </div>
        )}
      <div className='flex flex-col sm:flex-row gap-2 pt-1'>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('gitlab.tokens.namePlaceholder')}
          className='flex-1 text-xs'
        />
        <Button
          variant='secondary'
          size='sm'
          loading={mut.isPending}
          disabled={!name.trim()}
          onClick={() => {
            mut.mutate({ action: 'create', name: name.trim() }, {
              onSuccess: (r) => { if (r?.ok !== false) setName(''); },
            });
          }}
        >
          <Plus size={13} /> {t('gitlab.tokens.create')}
        </Button>
      </div>
    </div>
  );
}
